import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Tooltip,
  Typography,
} from '@mui/material'
import { Assignment, Visibility } from '@mui/icons-material'
import { DragDropContext, DropResult } from 'react-beautiful-dnd'
import { QuoteStatus } from '../../utils/QuoteStatus'
import moment from 'moment'
import ResponsiveAppBar from 'src/common/ResponsiveAppBar'
import {
  GetQuoteById,
  GetProfessionalComponentLineItems,
  GetTechnicalComponentLineItems,
  GetAnesthesiaLineItems,
  GetPhysicalTherapyLineItems,
  GetMiscellaneousLineItems,
  GetClaimButtonsWithSummary,
  ConsumerQuoteSaveRequest,
} from 'src/services/QuoteService'
import { processAction } from 'src/services/WorkflowService'
import {
  GetAllQuotesByPatientId,
  GetEOCPatientReferralInfo,
  GetQuotedTotal,
} from 'src/services/LookupService'
import { CostSourceText } from '../CostSourceText'
import type {
  IEditConsumerQuoteParams,
  LineItemType,
  LineItem,
  ClaimButton,
  QuoteData,
} from './types'
import {
  LINE_ITEM_TYPE_NAMES,
  LINE_ITEM_TYPE_ORDER,
  STEP3_PT_GRID_COLUMNS,
  STEP3_STANDARD_GRID_COLUMNS,
  EMPTY_ORIGINAL_QUOTE,
  EMPTY_CONSUMER_QUOTE,
} from './constants'
import {
  calculateTotal,
  formatCurrency,
  groupLineItemsByType,
  sortLineItemsByType,
  recalculateFinancials,
  normalizeQuoteId,
  mapClaimButton,
  mapStandardLineItem,
} from './utils'
import PatientHeader from './PatientHeader'
import QuoteCard from './QuoteCard'
import QuoteCardWithButtons from './QuoteCardWithButtons'
import QuoteTypeNameConstants from 'src/constants/QuoteTypeNameConstants'
import { DropdownOption } from 'src/viewModels/DropdownOption'
import { ObjectiveProcessAction } from 'src/viewModels/workflow/ObjectiveProcessAction'
import { TextField } from '@mui/material'
import * as yup from 'yup'

export default function EditConsumerQuote() {
  const { quoteId } = useParams<IEditConsumerQuoteParams>()
  const navigate = useNavigate()
  const location = useLocation()
  const returnPath =
    (location.state as { from?: string } | null)?.from ??
    `/workflow/quote/${quoteId}/default-inbox`

  const [loading, setLoading] = useState(true)
  const [quoteLoading, setQuoteLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [currentStep, setCurrentStep] = useState(1)
  const [originalQuote, setOriginalQuote] = useState<QuoteData | null>(null)
  const [consumerQuote, setConsumerQuote] = useState<QuoteData | null>(null)
  const [availableButtons, setAvailableButtons] = useState<ClaimButton[]>([])
  const [snackbarOpen, setSnackbarOpen] = useState(false)
  const [snackbarMessage, setSnackbarMessage] = useState('')
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success')
  const [selectedQuoteId, setSelectedQuoteId] = useState<string>('')
  const [hasUserSelectedQuote, setHasUserSelectedQuote] = useState(false)
  const [availableQuotes, setAvailableQuotes] = useState<DropdownOption[]>([])

  const savedScrollY = useRef(0)
  const pendingScrollRestore = useRef(false)
  const latestLoadRef = useRef<string>('')
  const initialRouteQuoteRef = useRef<Awaited<ReturnType<typeof GetQuoteById>> | null>(
    null
  )
  const preloadedQuoteRef = useRef<Awaited<ReturnType<typeof GetQuoteById>> | null>(null)
  const consumerBaselineRef = useRef<{
    totalOriginalCost: number
  } | null>(null)

  const [searchParams] = useSearchParams()
  const inboxItemId = searchParams.get('inboxItemId') ?? ''
  const actionId = searchParams.get('actionId') ?? ''
  const displayComment = searchParams.get('displayComment') ?? 'false'
  const commentRequired = searchParams.get('commentRequired') ?? 'false'

  const [comment, setComment] = useState('')
  const [commentError, setCommentError] = useState('')

  const commentSchema = yup.object({
    comment:
      commentRequired === 'true'
        ? yup.string().required('Comment is required')
        : yup.string(),
  })

  useLayoutEffect(() => {
    if (pendingScrollRestore.current) {
      window.scrollTo(0, savedScrollY.current)
      pendingScrollRestore.current = false
    }
  })

  const showSnackbar = useCallback((message: string, severity: 'success' | 'error') => {
    setSnackbarMessage(message)
    setSnackbarSeverity(severity)
    setSnackbarOpen(true)
  }, [])

  const buildBaselineFinancials = useCallback((quotedTotal: number) => {
    const totalOriginalCost = quotedTotal || 0

    return {
      totalOriginalCost,
    }
  }, [])

  const recalculateConsumerFromBaseline = useCallback((quote: QuoteData): QuoteData => {
    return {
      ...quote,
      consumedCost: calculateTotal(quote.lineItems),
    }
  }, [])

  useEffect(() => {
    let isMounted = true
    const loadQuoteOptions = async () => {
      if (!quoteId) {
        if (isMounted) setLoading(false)
        return
      }

      try {
        setLoading(true)
        const quoteData = await GetQuoteById(quoteId)
        const quotedTotal = await GetQuotedTotal(quoteId)

        initialRouteQuoteRef.current = quoteData
        preloadedQuoteRef.current = quoteData

        const baselineFinancials = buildBaselineFinancials(quotedTotal)
        consumerBaselineRef.current = baselineFinancials

        setConsumerQuote({
          id: quoteData.id || quoteId,
          title: 'Consumer Quote',
          patientName: quoteData.patientName || '',
          dateOfBirth: quoteData.dateOfBirth
            ? new Date(quoteData.dateOfBirth)
            : new Date(),
          bodyPart: quoteData.bodyPartName || '',
          eocLegacy: quoteData.episodeOfCareOrLegacyId || '',
          startDate: quoteData.startDate ? new Date(quoteData.startDate) : new Date(),
          endDate: quoteData.endDate ? new Date(quoteData.endDate) : new Date(),
          bundleDurationDays: quoteData.bundleDurationDays || 90,
          groupNumber: quoteData.groupNumber || 0,
          status: QuoteStatus.Draft,
          lineItems: [],
          totalOriginalCost: quoteData.adjustedCost || 0,
          consumedCost: 0,
          type:
            quoteData.type != null ? QuoteTypeNameConstants[quoteData.type] : 'Unknown',
        })

        const lookupPatientId = quoteData.episodeOfCareNumber
          ? (
              await GetEOCPatientReferralInfo(quoteData.episodeOfCareNumber)
            ).patientId?.trim()
          : undefined

        if (!isMounted) return

        if (lookupPatientId) {
          const relatedQuotes = await GetAllQuotesByPatientId(lookupPatientId)

          if (!isMounted) return

          const quoteOptions = relatedQuotes
            .filter((options) => options.id !== quoteId)
            .map((options) => ({
              id: options.id,
              description: options.description,
              isEnabled: options.isEnabled,
            }))

          setAvailableQuotes(quoteOptions)
        }
      } catch (error) {
        console.error('Failed to load quote options:', error)
        if (isMounted)
          showSnackbar('Error loading available quotes. Please try again.', 'error')
      } finally {
        if (isMounted) setLoading(false)
      }
    }

    loadQuoteOptions()
    return () => {
      isMounted = false
    }
  }, [quoteId, buildBaselineFinancials])

  useEffect(() => {
    if (!selectedQuoteId || !hasUserSelectedQuote) {
      setOriginalQuote(null)
      setAvailableButtons([])
      setCurrentStep(1)
      setQuoteLoading(false)
      return
    }

    loadQuoteData(selectedQuoteId, hasUserSelectedQuote)
  }, [selectedQuoteId, hasUserSelectedQuote])

  const loadQuoteData = async (quoteIdToLoad: string, isUserSelected: boolean) => {
    if (!quoteIdToLoad) {
      setLoading(false)
      return
    }

    const loadId = crypto.randomUUID()
    latestLoadRef.current = loadId

    try {
      setQuoteLoading(true)
      if (!isUserSelected) {
        setLoading(true)
      }

      const preloadedQuote = preloadedQuoteRef.current
      const hasPreloadedMatch =
        !!preloadedQuote &&
        normalizeQuoteId(preloadedQuote.id || quoteIdToLoad) ===
          normalizeQuoteId(quoteIdToLoad)

      const quoteData = hasPreloadedMatch
        ? preloadedQuote
        : await GetQuoteById(quoteIdToLoad)

      if (hasPreloadedMatch) {
        preloadedQuoteRef.current = null
      }

      const consumerHeaderData = initialRouteQuoteRef.current || quoteData
      if (!consumerBaselineRef.current) {
        const consumerQuoteId = consumerHeaderData.id || quoteId || quoteIdToLoad
        if (consumerQuoteId) {
          const quotedTotal = await GetQuotedTotal(consumerQuoteId)
          consumerBaselineRef.current = buildBaselineFinancials(quotedTotal)
        }
      }

      const [pcItems, tcItems, anItems, ptItems, miItems] = await Promise.all([
        GetProfessionalComponentLineItems(quoteIdToLoad),
        GetTechnicalComponentLineItems(quoteIdToLoad),
        GetAnesthesiaLineItems(quoteIdToLoad),
        GetPhysicalTherapyLineItems(quoteIdToLoad),
        GetMiscellaneousLineItems(quoteIdToLoad),
      ])

      const transformedLineItems: LineItem[] = [
        ...pcItems.map((item, index) =>
          mapStandardLineItem(
            item,
            index,
            'PC',
            'pc',
            'Professional Component',
            item.physicianName || ''
          )
        ),
        ...tcItems.map((item, index) =>
          mapStandardLineItem(item, index, 'TC', 'tc', 'Technical Component', '')
        ),
        ...anItems.map((item, index) =>
          mapStandardLineItem(
            item,
            index,
            'AN',
            'an',
            'Anesthesia',
            item.physicianName || ''
          )
        ),
        ...ptItems.map((item, index) => ({
          id: item.id || `pt-${index}-${crypto.randomUUID()}`,
          originalQuoteId: item.originalQuoteId,
          type: 'PT' as LineItemType,
          description: 'Physical Therapy',
          quantity: item.numberOfVisits || 1,
          cost: item.costPerVisit || 0,
          total: item.reconciledCost || item.cost || 0,
          providerGroup: item.providerName || '',
          providerType: '',
          cptCode: undefined,
          itemType: 'Physical Therapy',
          costSource: undefined,
          billedCharges: item.actualCostPerVisit
            ? item.actualCostPerVisit * (item.numberOfVisits || 1)
            : 0,
          quotedAmount: item.cost || 0,
          reconciledAmount: item.reconciledCost || 0,
          reasonCode: undefined,
          movedToQuoteLegacyEOCGroupNumber: item.movedToQuoteLegacyEOCGroupNumber,
          numberOfVisits: item.numberOfVisits,
          estUCPerVisit: item.actualCostPerVisit,
          estUCTotal: item.actualCostPerVisit
            ? item.actualCostPerVisit * (item.numberOfVisits || 1)
            : undefined,
          quotedPerVisit: item.costPerVisit,
        })),
        ...miItems.map((item, index) =>
          mapStandardLineItem(
            item,
            index,
            'MI',
            'mi',
            item.typeText || 'Miscellaneous',
            'Facility'
          )
        ),
      ]

      const originalQuoteData: QuoteData = {
        id: quoteData.id || quoteIdToLoad,
        title: 'Original Provider Quote',
        patientName: quoteData.patientName || '',
        dateOfBirth: quoteData.dateOfBirth ? new Date(quoteData.dateOfBirth) : new Date(),
        bodyPart: quoteData.bodyPartName || '',
        eocLegacy: quoteData.episodeOfCareOrLegacyId || '',
        startDate: quoteData.startDate ? new Date(quoteData.startDate) : new Date(),
        endDate: quoteData.endDate ? new Date(quoteData.endDate) : new Date(),
        bundleDurationDays: quoteData.bundleDurationDays || 90,
        groupNumber: quoteData.groupNumber || 0,
        status: quoteData.status || QuoteStatus.Draft,
        lineItems: sortLineItemsByType(transformedLineItems),
        totalOriginalCost: quoteData.adjustedCost || 0,
        consumedCost: 0,
        type: quoteData.type != null ? QuoteTypeNameConstants[quoteData.type] : 'Unknown',
      }

      if (latestLoadRef.current !== loadId) return

      setOriginalQuote(recalculateFinancials(originalQuoteData))

      const [pcButtons, tcButtons, anButtons, ptButtons, miButtons] = await Promise.all([
        GetClaimButtonsWithSummary(quoteIdToLoad, 'PC'),
        GetClaimButtonsWithSummary(quoteIdToLoad, 'TC'),
        GetClaimButtonsWithSummary(quoteIdToLoad, 'AN'),
        GetClaimButtonsWithSummary(quoteIdToLoad, 'PT'),
        GetClaimButtonsWithSummary(quoteIdToLoad, 'MI'),
      ])

      const allButtons: ClaimButton[] = [
        ...pcButtons.claimButtons.map((btn) => mapClaimButton(btn, 'PC')),
        ...tcButtons.claimButtons.map((btn) => mapClaimButton(btn, 'TC')),
        ...anButtons.claimButtons.map((btn) => mapClaimButton(btn, 'AN')),
        ...ptButtons.claimButtons.map((btn) => mapClaimButton(btn, 'PT')),
        ...miButtons.claimButtons.map((btn) => mapClaimButton(btn, 'MI')),
      ]

      if (latestLoadRef.current !== loadId) return

      setAvailableButtons(allButtons)

      const isConsumed = (item: {
        originalQuoteId?: string
        movedToQuoteLegacyEOCGroupNumber?: string
      }) => !!item.originalQuoteId || !!item.movedToQuoteLegacyEOCGroupNumber

      const hasMovableLineItems = transformedLineItems.some((item) => !isConsumed(item))
      if (!hasMovableLineItems) {
        setOriginalQuote((prev) => (prev ? { ...prev, lineItems: [] } : prev))
        setConsumerQuote((prev) =>
          prev
            ? recalculateConsumerFromBaseline({
                ...prev,
                lineItems: sortLineItemsByType(transformedLineItems),
              })
            : prev
        )
        setCurrentStep(2)
      }
    } catch (error) {
      console.error('Failed to load quote data:', error)
      showSnackbar('Error loading quote data. Please try again.', 'error')
    } finally {
      setQuoteLoading(false)
      if (!isUserSelected) {
        setLoading(false)
      }
    }
  }

  const onDragEnd = (result: DropResult) => {
    const { source, destination } = result

    if (!destination) return

    if (
      source.droppableId === destination.droppableId &&
      source.index === destination.index
    ) {
      return
    }

    if (currentStep === 1) {
      const sourceQuote =
        source.droppableId === 'original' ? originalQuote : consumerQuote
      const destQuote =
        destination.droppableId === 'original' ? originalQuote : consumerQuote

      if (!sourceQuote || !destQuote) return

      const sourceItems = Array.from(sourceQuote.lineItems)
      const destItems =
        source.droppableId === destination.droppableId
          ? sourceItems
          : Array.from(destQuote.lineItems)

      const [movedItem] = sourceItems.splice(source.index, 1)

      if (movedItem?.originalQuoteId) {
        return
      }

      if (source.droppableId === destination.droppableId) {
        sourceItems.splice(destination.index, 0, movedItem)
      } else {
        destItems.splice(destination.index, 0, movedItem)
      }

      if (source.droppableId === 'original') {
        const updatedOriginal = {
          ...originalQuote!,
          lineItems: sourceItems,
        }
        setOriginalQuote(updatedOriginal)
        if (destination.droppableId === 'consumer') {
          const updatedConsumer = recalculateConsumerFromBaseline({
            ...consumerQuote!,
            lineItems: destItems,
          })
          setConsumerQuote(updatedConsumer)
        }
      } else {
        const updatedConsumer = recalculateConsumerFromBaseline({
          ...consumerQuote!,
          lineItems: sourceItems,
        })
        setConsumerQuote(updatedConsumer)
        if (destination.droppableId === 'original') {
          const updatedOriginal = {
            ...originalQuote!,
            lineItems: destItems,
          }
          setOriginalQuote(updatedOriginal)
        }
      }
    }

    if (currentStep === 2) {
      const parseDroppableId = (id: string) => {
        const parts = id.split('-')
        if (parts.length === 3 && parts[1] === 'buttons') {
          return { quote: parts[0], type: parts[2] as LineItemType }
        }
        return null
      }

      const sourceParsed = parseDroppableId(source.droppableId)
      const destParsed = parseDroppableId(destination.droppableId)

      if (!sourceParsed || !destParsed) return

      const sourceButtons = availableButtons.filter((btn) => {
        if (sourceParsed.quote === 'original') {
          return btn.type === sourceParsed.type && btn.lineItemId === null
        } else {
          return btn.lineItemId === source.droppableId
        }
      })

      if (sourceButtons.length <= source.index) return

      const draggedButton = sourceButtons[source.index]
      if (draggedButton?.originalBundleQuoteId) return

      const newButtons = availableButtons.map((btn) => {
        if (btn.id === draggedButton.id) {
          return {
            ...btn,
            lineItemId:
              destParsed.quote === 'consumer'
                ? `consumer-buttons-${draggedButton.type}`
                : null,
          }
        }
        return btn
      })

      setAvailableButtons(newButtons)
    }
  }

  const handleNext = async () => {
    if (currentStep === 1) {
      setCurrentStep(2)
    } else if (currentStep === 2) {
      setCurrentStep(3)
    } else if (currentStep === 3) {
      try {
        await commentSchema.validate({ comment })
        setCommentError('')
        handleSave()
      } catch (err) {
        if (err instanceof yup.ValidationError) {
          setCommentError(err.message)
        }
      }
    }
  }

  const handleBack = () => {
    if (currentStep === 2) {
      const isConsumed = (item: {
        originalQuoteId?: string
        movedToQuoteLegacyEOCGroupNumber?: string
      }) => !!item.originalQuoteId || !!item.movedToQuoteLegacyEOCGroupNumber
      const nonConsumedInConsumer = (consumerQuote?.lineItems ?? []).filter(
        (item) => !isConsumed(item)
      )
      if (nonConsumedInConsumer.length > 0) {
        const consumedInConsumer = (consumerQuote?.lineItems ?? []).filter(isConsumed)
        setOriginalQuote((prev) =>
          prev
            ? {
                ...prev,
                lineItems: sortLineItemsByType([
                  ...prev.lineItems,
                  ...consumedInConsumer,
                ]),
              }
            : prev
        )
        setConsumerQuote((prev) =>
          prev
            ? recalculateConsumerFromBaseline({
                ...prev,
                lineItems: nonConsumedInConsumer,
              })
            : prev
        )
      } else {
        setSelectedQuoteId('')
        setHasUserSelectedQuote(false)
        setOriginalQuote(null)
        setAvailableButtons([])
        setConsumerQuote((prev) =>
          prev ? recalculateConsumerFromBaseline({ ...prev, lineItems: [] }) : prev
        )
      }
    }
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1)
    }
  }

  const handleSave = async () => {
    try {
      setSaving(true)
      const consumerQuoteIdToSave = quoteId || consumerQuote?.id
      if (!consumerQuoteIdToSave) {
        showSnackbar('Error saving consumer quote. Missing consumer quote ID.', 'error')
        return
      }

      if (!actionId || !inboxItemId) {
        showSnackbar(
          'Error saving consumer quote. Missing workflow context for process action.',
          'error'
        )
        return
      }

      const assignedButtons = availableButtons.filter((btn) => btn.lineItemId !== null)
      const consumerQuoteSaveRequest: ConsumerQuoteSaveRequest = {
        consumedQuoteId: selectedQuoteId,
        lineItemIds:
          consumerQuote?.lineItems.map((item) => ({
            id: item.id,
            type: item.type,
          })) ?? [],
        assignedClaimIds: assignedButtons.map((btn) => btn.id),
      }

      const data = new ObjectiveProcessAction()
      data.consumerQuoteSaveRequest = consumerQuoteSaveRequest
      data.comment = comment || undefined

      await processAction(data, actionId, inboxItemId)
      showSnackbar('Consumer quote saved successfully.', 'success')
      navigate(returnPath, {
        state: {
          forceReloadQuote: true,
        },
      })
    } catch {
      showSnackbar('Error saving consumer quote. Please try again.', 'error')
    } finally {
      setSaving(false)
    }
  }

  const toggleLineItemSelection = useCallback(
    (
      itemId: string,
      currentQuote: 'original' | 'consumer',
      event?: React.ChangeEvent<HTMLInputElement>
    ) => {
      savedScrollY.current = window.scrollY
      pendingScrollRestore.current = true
      if (event) {
        event.stopPropagation()
      }

      if (!originalQuote || !consumerQuote) return

      const sourceQuote = currentQuote === 'original' ? originalQuote : consumerQuote
      const destQuote = currentQuote === 'original' ? consumerQuote : originalQuote

      const itemToMove = sourceQuote.lineItems.find((item) => item.id === itemId)
      if (!itemToMove) return
      if (itemToMove.originalQuoteId) return

      const remainingItems = sourceQuote.lineItems.filter((item) => item.id !== itemId)
      const sortedDestItems = sortLineItemsByType([...destQuote.lineItems, itemToMove])

      if (currentQuote === 'original') {
        setOriginalQuote({ ...originalQuote, lineItems: remainingItems })
        setConsumerQuote(
          recalculateConsumerFromBaseline({
            ...consumerQuote,
            lineItems: sortedDestItems,
          })
        )
      } else {
        setConsumerQuote(
          recalculateConsumerFromBaseline({ ...consumerQuote, lineItems: remainingItems })
        )
        setOriginalQuote({ ...originalQuote, lineItems: sortedDestItems })
      }
    },
    [originalQuote, consumerQuote, recalculateConsumerFromBaseline]
  )

  const toggleButtonSelection = useCallback(
    (
      buttonId: string,
      currentLocation: 'original' | 'consumer',
      event?: React.ChangeEvent<HTMLInputElement>
    ) => {
      savedScrollY.current = window.scrollY
      pendingScrollRestore.current = true
      if (event) {
        event.stopPropagation()
      }

      const button = availableButtons.find((btn) => btn.id === buttonId)
      if (!button) return
      if (button.originalBundleQuoteId) return

      const newButtons = availableButtons.map((btn) => {
        if (btn.id === buttonId) {
          if (currentLocation === 'original') {
            return { ...btn, lineItemId: `consumer-buttons-${btn.type}` }
          } else {
            return { ...btn, lineItemId: null }
          }
        }
        return btn
      })

      setAvailableButtons(newButtons)
    },
    [availableButtons]
  )

  const consumeAll = useCallback(
    (from: 'original' | 'consumer') => {
      savedScrollY.current = window.scrollY
      pendingScrollRestore.current = true
      if (!originalQuote || !consumerQuote) return

      if (from === 'original') {
        const movableItems = originalQuote.lineItems.filter(
          (item) => !item.originalQuoteId
        )
        const lockedItems = originalQuote.lineItems.filter((item) => item.originalQuoteId)
        const allItems = sortLineItemsByType([
          ...consumerQuote.lineItems,
          ...movableItems,
        ])
        setOriginalQuote({ ...originalQuote, lineItems: lockedItems })
        setConsumerQuote(
          recalculateConsumerFromBaseline({ ...consumerQuote, lineItems: allItems })
        )
      } else {
        const movableItems = consumerQuote.lineItems.filter(
          (item) => !item.originalQuoteId
        )
        const lockedItems = consumerQuote.lineItems.filter((item) => item.originalQuoteId)
        const allItems = sortLineItemsByType([
          ...originalQuote.lineItems,
          ...movableItems,
        ])
        setConsumerQuote(
          recalculateConsumerFromBaseline({ ...consumerQuote, lineItems: lockedItems })
        )
        setOriginalQuote({ ...originalQuote, lineItems: allItems })
      }
    },
    [originalQuote, consumerQuote, recalculateConsumerFromBaseline]
  )

  const moveAllOfType = useCallback(
    (type: LineItemType, from: 'original' | 'consumer') => {
      savedScrollY.current = window.scrollY
      pendingScrollRestore.current = true
      if (!originalQuote || !consumerQuote) return

      const sourceItems =
        from === 'original' ? originalQuote.lineItems : consumerQuote.lineItems
      const itemsOfType = sourceItems.filter((item) => item.type === type)
      const remainingItems = sourceItems.filter((item) => item.type !== type)

      if (from === 'original') {
        const destItems = sortLineItemsByType([
          ...consumerQuote.lineItems,
          ...itemsOfType,
        ])
        setOriginalQuote({ ...originalQuote, lineItems: remainingItems })
        setConsumerQuote(
          recalculateConsumerFromBaseline({ ...consumerQuote, lineItems: destItems })
        )
      } else {
        const destItems = sortLineItemsByType([
          ...originalQuote.lineItems,
          ...itemsOfType,
        ])
        setConsumerQuote(
          recalculateConsumerFromBaseline({ ...consumerQuote, lineItems: remainingItems })
        )
        setOriginalQuote({ ...originalQuote, lineItems: destItems })
      }
    },
    [originalQuote, consumerQuote, recalculateConsumerFromBaseline]
  )

  const assignAllButtons = useCallback(
    (type: LineItemType, from: 'original' | 'consumer') => {
      savedScrollY.current = window.scrollY
      pendingScrollRestore.current = true
      const consumerHasTypeItems = (consumerQuote?.lineItems || []).some(
        (item) => item.type === type
      )
      setAvailableButtons((prev) =>
        prev.map((btn) => {
          if (btn.type !== type) return btn
          if (btn.originalBundleQuoteId) return btn
          if (from === 'original' && btn.lineItemId === null && consumerHasTypeItems) {
            return { ...btn, lineItemId: `consumer-buttons-${type}` }
          }
          if (from === 'consumer' && btn.lineItemId === `consumer-buttons-${type}`) {
            return { ...btn, lineItemId: null }
          }
          return btn
        })
      )
    },
    [consumerQuote]
  )

  const assignAllClaimButtons = useCallback(
    (from: 'original' | 'consumer') => {
      savedScrollY.current = window.scrollY
      pendingScrollRestore.current = true
      const consumerLineItemTypes = new Set(
        (consumerQuote?.lineItems || []).map((item) => item.type)
      )
      setAvailableButtons((prev) =>
        prev.map((btn) => {
          if (btn.originalBundleQuoteId) return btn
          if (
            from === 'original' &&
            btn.lineItemId === null &&
            consumerLineItemTypes.has(btn.type)
          ) {
            return { ...btn, lineItemId: `consumer-buttons-${btn.type}` }
          }
          if (from === 'consumer' && btn.lineItemId !== null) {
            return { ...btn, lineItemId: null }
          }
          return btn
        })
      )
    },
    [consumerQuote]
  )

  const hasAvailableQuotes = availableQuotes.length > 0
  const selectedQuoteValue = useMemo(() => {
    const normalizedId = normalizeQuoteId(selectedQuoteId)
    return availableQuotes.find((q) => normalizeQuoteId(q.id) === normalizedId)?.id ?? ''
  }, [availableQuotes, selectedQuoteId])
  const selectedQuoteLabel = useMemo(
    () =>
      availableQuotes.find(
        (q) => normalizeQuoteId(q.id) === normalizeQuoteId(selectedQuoteValue)
      )?.description ?? '',
    [availableQuotes, selectedQuoteValue]
  )

  const step3GroupedItems = useMemo(
    () =>
      groupLineItemsByType(
        (consumerQuote?.lineItems ?? []).filter(
          (item) => !item.originalQuoteId && !item.movedToQuoteLegacyEOCGroupNumber
        )
      ),
    [consumerQuote]
  )
  const step3AssignedButtons = useMemo(
    () => availableButtons.filter((btn) => btn.lineItemId !== null),
    [availableButtons]
  )
  const hasMovableAssignableButtonsInStep2 = useMemo(() => {
    const consumerLineItemTypes = new Set(
      (consumerQuote?.lineItems || []).map((item) => item.type)
    )

    return availableButtons.some((btn) => {
      if (btn.originalBundleQuoteId) return false
      if (btn.lineItemId !== null) return true
      return consumerLineItemTypes.has(btn.type)
    })
  }, [availableButtons, consumerQuote])

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress size={60} />
      </Box>
    )
  }

  const displayOriginalQuote = originalQuote || EMPTY_ORIGINAL_QUOTE
  const displayConsumerQuote = consumerQuote || EMPTY_CONSUMER_QUOTE

  return (
    <>
      <ResponsiveAppBar title={`Edit Consumer Quote Wizard - Step ${currentStep} of 3`} />

      <DragDropContext onDragEnd={onDragEnd}>
        <Box sx={{ p: 3 }}>
          {/* Header */}
          <Box
            sx={{
              mb: 3,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Box>
              <Typography variant="h4" color="text.secondary">
                Step {currentStep} of 3:{' '}
                {currentStep === 1
                  ? 'Select Line Items'
                  : currentStep === 2
                    ? 'Assign Claim Buttons'
                    : 'Review Consumer Quote'}
              </Typography>
            </Box>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {currentStep === 1
              ? 'Drag and drop line items between quotes, or check the checkbox to move items to the Consumer Quote.'
              : currentStep === 2
                ? 'Drag claim buttons between quote sections, or check the checkbox to move buttons to the Consumer Quote.'
                : 'Review the consumer quote details before saving.'}
          </Typography>

          <PatientHeader quote={displayConsumerQuote} />

          {/* Quote Selection - Step 1 Only */}
          {currentStep === 1 && (
            <Box
              sx={{
                mb: 3,
                display: 'flex',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontWeight: 'bold' }}
              >
                Source Quote:
              </Typography>
              <FormControl size="small" sx={{ minWidth: 600 }}>
                <InputLabel id="quote-select-label" shrink>
                  Select Quote to Consume
                </InputLabel>
                <Select
                  labelId="quote-select-label"
                  value={selectedQuoteValue}
                  label="Select Quote to Consume"
                  displayEmpty
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        minWidth: 670,
                      },
                    },
                  }}
                  disabled={
                    !hasAvailableQuotes ||
                    quoteLoading ||
                    (consumerQuote?.lineItems.length ?? 0) > 0
                  }
                  renderValue={() =>
                    hasAvailableQuotes ? selectedQuoteLabel || '' : 'No options'
                  }
                  onChange={(e) => {
                    setQuoteLoading(true)
                    setHasUserSelectedQuote(true)
                    setSelectedQuoteId(e.target.value)
                  }}
                >
                  {hasAvailableQuotes ? (
                    availableQuotes.map((quote) => (
                      <MenuItem key={quote.id} value={quote.id}>
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                          <Typography variant="body2" noWrap>
                            {quote.description}
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem value="" disabled>
                      No options
                    </MenuItem>
                  )}
                </Select>
              </FormControl>
            </Box>
          )}

          {/* Step 1: Select Line Items */}
          {currentStep === 1 && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                {hasUserSelectedQuote ? (
                  quoteLoading ? (
                    <Card elevation={3}>
                      <CardHeader
                        title={displayOriginalQuote.title}
                        avatar={<Assignment color="primary" />}
                      />
                      <CardContent>
                        <Box
                          sx={{
                            minHeight: 220,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <CircularProgress size={40} />
                        </Box>
                      </CardContent>
                    </Card>
                  ) : (
                    <QuoteCard
                      quote={displayOriginalQuote}
                      droppableId="original"
                      onToggleItem={toggleLineItemSelection}
                      onMoveAllOfType={moveAllOfType}
                      onMoveAll={consumeAll}
                    />
                  )
                ) : (
                  <Card elevation={3}>
                    <CardHeader
                      title={displayOriginalQuote.title}
                      avatar={<Assignment color="primary" />}
                    />
                    <CardContent>
                      <Typography color="text.secondary" variant="body2">
                        Select a quote from the dropdown to view Original Provider Quote
                        details.
                      </Typography>
                    </CardContent>
                  </Card>
                )}
              </Grid>
              <Grid item xs={12} md={6}>
                <QuoteCard
                  quote={displayConsumerQuote}
                  droppableId="consumer"
                  onToggleItem={toggleLineItemSelection}
                  onMoveAllOfType={moveAllOfType}
                  onMoveAll={consumeAll}
                />
              </Grid>
            </Grid>
          )}

          {/* Step 2: Assign Claim Buttons */}
          {currentStep === 2 && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <QuoteCardWithButtons
                  quote={displayOriginalQuote}
                  quoteType="original"
                  availableButtons={availableButtons}
                  consumerQuote={displayConsumerQuote}
                  onToggleButton={toggleButtonSelection}
                  onAssignAll={assignAllButtons}
                  onAssignAllClaims={assignAllClaimButtons}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <QuoteCardWithButtons
                  quote={displayConsumerQuote}
                  quoteType="consumer"
                  availableButtons={availableButtons}
                  consumerQuote={displayConsumerQuote}
                  onToggleButton={toggleButtonSelection}
                  onAssignAll={assignAllButtons}
                  onAssignAllClaims={assignAllClaimButtons}
                />
              </Grid>
            </Grid>
          )}

          {/* Step 3: Review Consumer Quote */}
          {currentStep === 3 && (
            <Box>
              <Card elevation={3}>
                <CardHeader
                  title="Consumer Quote Summary"
                  avatar={<Assignment color="primary" />}
                  subheader="Review what you are moving to the consumer quote before saving"
                />
                <CardContent>
                  <Grid container spacing={3} sx={{ mb: 3 }}>
                    <Grid item xs={12} md={2}>
                      <Typography variant="caption" color="text.secondary">
                        <strong>Type:</strong>
                      </Typography>
                      <Typography variant="body2">{displayConsumerQuote.type}</Typography>
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <Typography variant="caption" color="text.secondary">
                        <strong>Start Date:</strong>
                      </Typography>
                      <Typography variant="body2">
                        {moment(displayConsumerQuote.startDate).format('L')}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <Typography variant="caption" color="text.secondary">
                        <strong>End Date:</strong>
                      </Typography>
                      <Typography variant="body2">
                        {moment(displayConsumerQuote.endDate).format('L')}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <Typography variant="caption" color="text.secondary">
                        <strong>Bundle Duration:</strong>
                      </Typography>
                      <Typography variant="body2">
                        {displayConsumerQuote.bundleDurationDays} days
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <Typography variant="caption" color="text.secondary">
                        <strong>Group Number:</strong>
                      </Typography>
                      <Typography variant="body2">
                        {displayConsumerQuote.groupNumber}
                      </Typography>
                    </Grid>
                  </Grid>

                  <Divider sx={{ my: 2 }} />

                  {LINE_ITEM_TYPE_ORDER.map((type) => {
                    const items = step3GroupedItems[type]
                    const typeButtons = step3AssignedButtons.filter(
                      (btn) => btn.type === type
                    )

                    if (items.length === 0 && typeButtons.length === 0) return null

                    return (
                      <Box key={type} sx={{ mb: 3 }}>
                        <Box
                          sx={{
                            bgcolor: 'primary.main',
                            color: 'primary.contrastText',
                            p: 1.5,
                            borderRadius: 1,
                            mb: 2,
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                          }}
                        >
                          <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                            {items[0]?.itemType || LINE_ITEM_TYPE_NAMES[type]}
                          </Typography>
                          {typeButtons.length > 0 && (
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                              {typeButtons.length}{' '}
                              {typeButtons.length === 1
                                ? 'assigned claim'
                                : 'assigned claims'}
                            </Typography>
                          )}
                        </Box>

                        {items.length === 0 ? (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ px: 1.5, py: 1 }}
                          >
                            $0.00
                          </Typography>
                        ) : (
                          <>
                            {/* Column Headers */}
                            <Box
                              sx={{
                                display: 'grid',
                                gridTemplateColumns:
                                  type === 'PT'
                                    ? STEP3_PT_GRID_COLUMNS
                                    : STEP3_STANDARD_GRID_COLUMNS,
                                bgcolor: 'background.default',
                                borderBottom: '2px solid',
                                borderColor: 'primary.light',
                                mb: 1,
                              }}
                            >
                              {type === 'PT' ? (
                                <>
                                  {(
                                    [
                                      'Provider',
                                      '# Visits',
                                      'Est. U&C/Visit',
                                      'Est. U&C $',
                                      'Quoted/Visit',
                                      'Quoted $',
                                    ] as const
                                  ).map((label, i, arr) => (
                                    <Typography
                                      key={label}
                                      variant="caption"
                                      fontWeight="bold"
                                      color="text.secondary"
                                      sx={{
                                        px: 1.5,
                                        py: 1,
                                        textAlign: i > 0 ? 'right' : 'left',
                                        borderRight:
                                          i < arr.length - 1 ? '1px solid' : 'none',
                                        borderColor: 'divider',
                                      }}
                                    >
                                      {label}
                                    </Typography>
                                  ))}
                                </>
                              ) : (
                                <>
                                  {(
                                    [
                                      'Provider',
                                      'Physician',
                                      'Type',
                                      'CPT',
                                      'Cost Source',
                                      'Charges $',
                                      'Quoted $',
                                    ] as const
                                  ).map((label, i, arr) => (
                                    <Typography
                                      key={label}
                                      variant="caption"
                                      fontWeight="bold"
                                      color="text.secondary"
                                      sx={{
                                        px: 1.5,
                                        py: 1,
                                        textAlign: i >= 5 && i <= 6 ? 'right' : 'left',
                                        borderRight:
                                          i < arr.length - 1 ? '1px solid' : 'none',
                                        borderColor: 'divider',
                                      }}
                                    >
                                      {label}
                                    </Typography>
                                  ))}
                                </>
                              )}
                            </Box>

                            {/* Line Items */}
                            {items.map((item) => (
                              <Box
                                key={item.id}
                                sx={{
                                  display: 'grid',
                                  gridTemplateColumns:
                                    item.type === 'PT'
                                      ? STEP3_PT_GRID_COLUMNS
                                      : STEP3_STANDARD_GRID_COLUMNS,
                                  mb: 0,
                                  borderBottom: '1px solid',
                                  borderColor: 'divider',
                                  '&:hover': { bgcolor: 'action.hover' },
                                  '&:last-child': { borderBottom: 'none' },
                                }}
                              >
                                {item.type === 'PT' ? (
                                  <>
                                    {[
                                      item.providerGroup,
                                      item.numberOfVisits || 0,
                                      `$${formatCurrency(item.estUCPerVisit || 0)}`,
                                      `$${formatCurrency(item.estUCTotal || 0)}`,
                                      `$${formatCurrency(item.quotedPerVisit || 0)}`,
                                      `$${formatCurrency(item.quotedAmount)}`,
                                    ].map((val, i, arr) => (
                                      <Typography
                                        key={i}
                                        variant="body2"
                                        noWrap={i === 0}
                                        sx={{
                                          px: 1.5,
                                          py: 1.25,
                                          textAlign: i > 0 ? 'right' : 'left',
                                          borderRight:
                                            i < arr.length - 1 ? '1px solid' : 'none',
                                          borderColor: 'divider',
                                        }}
                                      >
                                        {val}
                                      </Typography>
                                    ))}
                                  </>
                                ) : (
                                  <>
                                    {[
                                      item.providerGroup,
                                      item.providerType || '-',
                                      item.itemType,
                                      item.cptCode || '-',
                                      item.costSource !== undefined
                                        ? CostSourceText[item.costSource]
                                        : '-',
                                      `$${formatCurrency(item.billedCharges)}`,
                                      `$${formatCurrency(item.quotedAmount)}`,
                                    ].map((val, i, arr) => (
                                      <Typography
                                        key={i}
                                        variant="body2"
                                        noWrap={i <= 4}
                                        title={typeof val === 'string' ? val : undefined}
                                        sx={{
                                          px: 1.5,
                                          py: 1.25,
                                          textAlign: i >= 5 && i <= 6 ? 'right' : 'left',
                                          borderRight:
                                            i < arr.length - 1 ? '1px solid' : 'none',
                                          borderColor: 'divider',
                                        }}
                                      >
                                        {val}
                                      </Typography>
                                    ))}
                                  </>
                                )}
                              </Box>
                            ))}
                          </>
                        )}

                        {/* Claim Buttons for this type */}
                        {typeButtons.length > 0 && (
                          <Box sx={{ mt: 2, pl: 2 }}>
                            <Typography
                              variant="subtitle2"
                              color="text.secondary"
                              gutterBottom
                            >
                              Assigned Claims:
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                              {typeButtons.map((button) => {
                                const isAppeal =
                                  button.appealTypeDescription !== undefined
                                const appealText = isAppeal
                                  ? `[${button.appealTypeDescription}] `
                                  : ''
                                return (
                                  <Tooltip
                                    key={button.id}
                                    title={
                                      <span>
                                        {button.percentageLineItemsReconciled !==
                                        undefined
                                          ? `${appealText}${button.providerGroup} - ${button.percentageLineItemsReconciled}% Reconciled`
                                          : `${appealText}${button.providerGroup}`}
                                        <br />
                                        DOS:{' '}
                                        {button.dateOfService
                                          ? button.dateOfService
                                          : '[UNKNOWN]'}
                                      </span>
                                    }
                                    disableInteractive
                                  >
                                    <Button
                                      sx={
                                        isAppeal
                                          ? {
                                              backgroundColor: 'error.main',
                                              color: 'error.contrastText',
                                              '&:hover': {
                                                backgroundColor: 'error.dark',
                                              },
                                            }
                                          : {}
                                      }
                                      variant={
                                        button.percentageLineItemsReconciled === 100
                                          ? 'contained'
                                          : 'outlined'
                                      }
                                      size="small"
                                      color={isAppeal ? undefined : 'primary'}
                                    >
                                      <Visibility fontSize="small" />
                                      &nbsp;{button.name}
                                    </Button>
                                  </Tooltip>
                                )
                              })}
                            </Box>
                          </Box>
                        )}
                      </Box>
                    )
                  })}

                  <Divider sx={{ my: 3 }} />

                  {/* Total */}
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      p: 2,
                      bgcolor: 'action.hover',
                      borderRadius: 1,
                    }}
                  >
                    <Typography variant="h5" fontWeight="bold">
                      Original Quoted Cost:
                    </Typography>
                    <Typography variant="h4" fontWeight="bold" color="primary">
                      ${formatCurrency(displayConsumerQuote.totalOriginalCost)}
                    </Typography>
                    <Typography variant="h5" fontWeight="bold">
                      Total Consumed:
                    </Typography>
                    <Typography variant="h4" fontWeight="bold" color="primary">
                      ${formatCurrency(displayConsumerQuote.consumedCost)}
                    </Typography>
                    <Typography variant="h5" fontWeight="bold">
                      Total After Consumed:
                    </Typography>
                    <Typography variant="h4" fontWeight="bold" color="primary">
                      $
                      {formatCurrency(
                        displayConsumerQuote.consumedCost +
                          displayConsumerQuote.totalOriginalCost
                      )}
                    </Typography>
                  </Box>

                  {/* Summary Stats */}
                  <Grid container spacing={2} sx={{ mt: 2 }}>
                    <Grid item xs={12} md={4}>
                      <Paper elevation={2} sx={{ p: 2, textAlign: 'center' }}>
                        <Typography variant="h4" color="primary" fontWeight="bold">
                          {
                            displayConsumerQuote.lineItems.filter(
                              (item) =>
                                !item.originalQuoteId &&
                                !item.movedToQuoteLegacyEOCGroupNumber
                            ).length
                          }
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Line Items
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Paper elevation={2} sx={{ p: 2, textAlign: 'center' }}>
                        <Typography variant="h4" color="primary" fontWeight="bold">
                          {
                            availableButtons.filter((btn) => btn.lineItemId !== null)
                              .length
                          }
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Claim Buttons Assigned
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Paper elevation={2} sx={{ p: 2, textAlign: 'center' }}>
                        <Typography variant="h4" color="primary" fontWeight="bold">
                          {
                            new Set(
                              displayConsumerQuote.lineItems
                                .filter(
                                  (item) =>
                                    !item.originalQuoteId &&
                                    !item.movedToQuoteLegacyEOCGroupNumber
                                )
                                .map((item) => item.type)
                            ).size
                          }
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Service Types
                        </Typography>
                      </Paper>
                    </Grid>
                  </Grid>
                  {displayComment === 'true' && (
                    <Grid item xs={12} sx={{ mt: 2 }}>
                      <TextField
                        inputProps={{ id: 'comments', name: 'comments' }}
                        InputLabelProps={{ shrink: true }}
                        label="Comments"
                        type="text"
                        variant="outlined"
                        fullWidth
                        rows={4}
                        multiline
                        value={comment}
                        onChange={(e) => {
                          setComment(e.target.value)
                          if (commentError) setCommentError('')
                        }}
                        error={!!commentError}
                        helperText={commentError}
                        required={commentRequired === 'true'}
                      />
                    </Grid>
                  )}
                </CardContent>
              </Card>
            </Box>
          )}

          {/* Wizard Navigation Buttons */}
          <Box
            sx={{
              mt: 4,
              display: 'flex',
              justifyContent: 'space-between',
              borderTop: '1px solid',
              borderColor: 'divider',
              pt: 3,
            }}
          >
            <Button
              variant="outlined"
              color="primary"
              onClick={() => navigate(returnPath)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Box sx={{ display: 'flex', gap: 2 }}>
              {currentStep > 1 && (
                <Button
                  variant="outlined"
                  onClick={handleBack}
                  disabled={
                    saving ||
                    (currentStep === 2 &&
                      availableButtons.some((btn) => btn.lineItemId !== null) &&
                      !(consumerQuote?.lineItems ?? []).some(
                        (item) =>
                          !item.originalQuoteId && !item.movedToQuoteLegacyEOCGroupNumber
                      ))
                  }
                >
                  Back
                </Button>
              )}
              <Button
                variant="contained"
                color="primary"
                onClick={handleNext}
                disabled={
                  saving ||
                  !hasUserSelectedQuote ||
                  (currentStep === 1 && displayConsumerQuote.lineItems.length === 0) ||
                  (currentStep === 2 &&
                    hasMovableAssignableButtonsInStep2 &&
                    !availableButtons.some((btn) => btn.lineItemId !== null))
                }
                startIcon={
                  saving && currentStep === 3 ? <CircularProgress size={20} /> : undefined
                }
              >
                {currentStep === 3 ? (saving ? 'Saving...' : 'Save Quote') : 'Next'}
              </Button>
            </Box>
          </Box>
        </Box>
      </DragDropContext>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={5000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          variant="filled"
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  )
}
